import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter(
    (a) => article.relatedArticles.includes(a.slug)
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '亚洲春色',
      url: 'https://dongyihan88.cn',
    },
    url: `https://dongyihan88.cn/article/${article.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://dongyihan88.cn/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="pt-16">
        <article>
          <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 亚洲春色`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-4xl mx-auto">
                <span className="inline-block px-3 py-1 text-xs rounded-full bg-background-50/90 text-foreground-700 mb-3 whitespace-nowrap">
                  {article.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-white/70 text-sm">
                  <span>{article.author}</span>
                  <span>{article.date}</span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line text-xs"></i>
                    </span>
                    {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 md:px-10 py-8 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: article.category, to: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />

              <div className="mt-8 prose prose-lg max-w-none">
                {article.content.map((section, idx) => {
                  switch (section.type) {
                    case 'heading':
                      return (
                        <h2 key={idx} className="text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                          {section.content}
                        </h2>
                      );
                    case 'paragraph':
                      return (
                        <p key={idx} className="text-foreground-700 leading-relaxed mb-5 text-sm md:text-base">
                          {section.content}
                        </p>
                      );
                    case 'image':
                      return (
                        <figure key={idx} className="my-8 -mx-4 md:mx-0">
                          <img
                            src={section.content}
                            alt=""
                            className="w-full rounded-lg"
                          />
                        </figure>
                      );
                    case 'quote':
                      return (
                        <blockquote key={idx} className="my-8 pl-6 border-l-4 border-primary-400 py-3 bg-background-100 rounded-r-lg">
                          <p className="text-foreground-600 italic text-sm md:text-base leading-relaxed">
                            {section.content}
                          </p>
                        </blockquote>
                      );
                    case 'list':
                      return (
                        <ul key={idx} className="space-y-2 mb-5">
                          {section.items?.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm md:text-base text-foreground-700">
                              <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 flex-shrink-0">
                                <i className="ri-check-line"></i>
                              </span>
                              <span className="leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    default:
                      return null;
                  }
                })}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/60 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-16 px-6 md:px-10 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}